import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const BANNER_STORAGE_KEY = "helpgenie.installBanner.dismissedAt";
const PRIVATE_ROUTE_ONCE_KEY = "helpgenie.installBanner.privateRouteShown";
const CONVERSATION_ONCE_KEY = "helpgenie.installBanner.conversationShown";
const BANNER_DELAY_MS = 10000;
const BANNER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

type BannerSource = "public-route" | "private-route" | "conversation" | null;

interface InstallAppBannerContextValue {
  isOpen: boolean;
  dismissBanner: () => void;
  suppressBanner: () => void;
  handleRouteChange: (isPrivateRoute: boolean) => void;
  showAfterConversationOnce: () => boolean;
}

const InstallAppBannerContext = createContext<InstallAppBannerContextValue | undefined>(undefined);

const isLikelyIosSafari = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);

  return isIos && isSafari;
};

const isBannerSupported = () => {
  if (typeof window === "undefined") return false;
  // iOS Safari has native Smart App Banner.
  if (isLikelyIosSafari()) return false;
  // Show banner on Android and all other supported browsers/devices.
  return true;
};

const canShowPublicByCooldown = () => {
  if (typeof window === "undefined") return false;
  const dismissedAtRaw = window.localStorage.getItem(BANNER_STORAGE_KEY);
  if (!dismissedAtRaw) return true;

  const dismissedAt = Number(dismissedAtRaw);
  if (!Number.isFinite(dismissedAt)) return true;

  return Date.now() - dismissedAt >= BANNER_COOLDOWN_MS;
};

export const InstallAppBannerProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const sourceRef = useRef<BannerSource>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const openFromSource = useCallback((source: Exclude<BannerSource, null>) => {
    sourceRef.current = source;
    setIsOpen(true);
  }, []);

  const dismissBanner = useCallback(() => {
    if (typeof window !== "undefined") {
      const now = String(Date.now());
      window.localStorage.setItem(BANNER_STORAGE_KEY, now);

      if (sourceRef.current === "private-route") {
        window.localStorage.setItem(PRIVATE_ROUTE_ONCE_KEY, "1");
      }
      if (sourceRef.current === "conversation") {
        window.localStorage.setItem(CONVERSATION_ONCE_KEY, "1");
      }
    }

    clearTimer();
    sourceRef.current = null;
    setIsOpen(false);
  }, [clearTimer]);

  const suppressBanner = useCallback(() => {
    clearTimer();
    sourceRef.current = null;
    setIsOpen(false);
  }, [clearTimer]);

  const handleRouteChange = useCallback(
    (isPrivateRoute: boolean) => {
      clearTimer();
      if (!isBannerSupported() || isOpen) return;

      if (isPrivateRoute) {
        const alreadyShown = window.localStorage.getItem(PRIVATE_ROUTE_ONCE_KEY) === "1";
        if (alreadyShown) return;

        timeoutRef.current = window.setTimeout(() => {
          openFromSource("private-route");
        }, BANNER_DELAY_MS);
        return;
      }

      if (!canShowPublicByCooldown()) return;

      timeoutRef.current = window.setTimeout(() => {
        openFromSource("public-route");
      }, BANNER_DELAY_MS);
    },
    [clearTimer, isOpen, openFromSource],
  );

  const showAfterConversationOnce = useCallback(() => {
    if (!isBannerSupported()) return false;
    if (typeof window === "undefined") return false;
    if (isOpen) return false;

    const alreadyShown = window.localStorage.getItem(CONVERSATION_ONCE_KEY) === "1";
    if (alreadyShown) return false;

    clearTimer();
    openFromSource("conversation");
    return true;
  }, [clearTimer, isOpen, openFromSource]);

  const value = useMemo<InstallAppBannerContextValue>(
    () => ({
      isOpen,
      dismissBanner,
      suppressBanner,
      handleRouteChange,
      showAfterConversationOnce,
    }),
    [dismissBanner, suppressBanner, handleRouteChange, isOpen, showAfterConversationOnce],
  );

  return (
    <InstallAppBannerContext.Provider value={value}>{children}</InstallAppBannerContext.Provider>
  );
};

export const useInstallAppBanner = () => {
  const ctx = useContext(InstallAppBannerContext);
  if (!ctx) {
    throw new Error("useInstallAppBanner must be used within InstallAppBannerProvider");
  }
  return ctx;
};

